import Button from 'components/atoms/Button/Button';
import LocaleAwareImage from 'components/atoms/LocaleAwareImage/LocaleAwareImage';
import RegistrationCard from 'components/atoms/RegistrationCard/RegistrationCard';
import TextInput from 'components/atoms/TextInput/TextInput';
import Txt from 'components/atoms/Txt/Txt';
import Form from 'components/molecules/Form/Form';
import useFrontEndEventTrigger from 'hooks/useFrontEndEventTrigger';
import useUserSourceData from 'hooks/useUserSourceData';
import React, { useRef, useState } from 'react';
import { NAME_REGEX } from 'utils/formHelper';
import { StepProps } from './RegistrationForm';
import classes from './RegistrationForm.module.scss';

export function BasicDetailsStep({
  onNext,
  onPrevious,
  currentFormData,
  progressIndicator,
  error,
  menu,
}: StepProps) {
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const [dateError, setDateError] = useState<string | undefined>(undefined);
  const { frontEndEventTrigger } = useFrontEndEventTrigger();

  const isSocial = Boolean(currentFormData.provider || currentFormData.socialToken);

  const { contactEmail, firstName, lastName } = useUserSourceData();

  const isJoinApi = Boolean(contactEmail && !currentFormData.socialToken);

  function handleSubmit(formData: FormData) {
    onNext?.({
      firstName: formData.get('firstName')?.toString() ?? '',
      lastName: formData.get('lastName')?.toString() ?? '',
      dateOfBirth: new Date(
        Date.UTC(
          parseInt(formData.get('year')?.toString() ?? '', 10),
          parseInt(formData.get('month')?.toString() ?? '', 10) - 1,
          parseInt(formData.get('day')?.toString() ?? '', 10),
        ),
      ),
    });
    frontEndEventTrigger('Registration step', 'Basic details');
  }

  function handlePrevious(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    onPrevious?.();
  }

  function handleDOBChange(e: React.FormEvent<HTMLInputElement>) {
    // Handles automatically moving the user's focus on to the next DOB input
    if (e.currentTarget.value.length === 2 && e.currentTarget.checkValidity()) {
      if (dayRef.current && dayRef.current.contains(e.currentTarget)) {
        monthRef.current?.focus();
      } else if (monthRef.current && monthRef.current.contains(e.currentTarget)) {
        yearRef.current?.focus();
      } else if (yearRef.current && yearRef.current.contains(e.currentTarget)) {
        yearRef.current?.blur();
      }
    }
  }

  function validateDOB(e: React.FormEvent<HTMLInputElement>) {
    if (
      dayRef.current?.value.length === 0 ||
      monthRef.current?.value.length === 0 ||
      yearRef.current?.value.length === 0
    ) {
      // User input may still be in progress.
      return;
    }

    if (!e.currentTarget.checkValidity()) {
      setDateError('Invalid date');
      return;
    }

    const day = parseInt(dayRef.current?.value ?? '', 10);
    const month = parseInt(monthRef.current?.value ?? '', 10);
    const year = parseInt(yearRef.current?.value ?? '', 10);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCDate() !== day ||
      date.getUTCMonth() + 1 !== month ||
      date.getUTCFullYear() !== year
    ) {
      setDateError('Invalid date');
    } else if (date.getTime() > Date.now() - 1000 * 60 * 60 * 24 * 365 * 16) {
      setDateError('You must be at least 16 years old');
    } else if (date.getTime() < Date.now() - 1000 * 60 * 60 * 24 * 365 * 99) {
      setDateError('You must be less than 99 years old');
    } else setDateError(undefined);
  }

  function customErrorMessages(validity: ValidityState, value: string) {
    if (validity.patternMismatch) {
      return 'Use letters and special symbols . - _';
    } else if (validity.valueMissing) {
      return 'This field is required';
    }
  }

  return (
    <div className={classes.registrationFormContainer}>
      <RegistrationCard
        title="Create your account"
        menu={menu}
        progressIndicator={progressIndicator}
        additionalInfo={{
          title: 'Influence brands with your opinions',
          subtitle: (
            <>
              <Txt component={'p'}>
                With LifePoints you get to tell brands what you want from them and help shape your
                favourite products.
              </Txt>
              <Txt component={'p'}>Have your voice heard and get rewarded on top!</Txt>
            </>
          ),
          image: (
            <LocaleAwareImage
              alt=""
              src="/assets/registration-details.png"
              width={431}
              height={234}
            />
          ),
        }}
      >
        {error}
        <Txt>Let&apos;s get your account all set up. We just need a few details from you.</Txt>
        <Form
          submitDisabled={dateError !== undefined}
          submitName="Next"
          submitButtonType="primary"
          onSubmit={handleSubmit}
          className={classes.formOverrides}
          buttonWrapperClassName={classes.buttonWrapper}
          submitButtonClassName={classes.button}
          extraActionButtons={
            <>
              {!isJoinApi && (
                <Button
                  buttonSize="large"
                  buttonType="text"
                  className={classes.button}
                  onClick={handlePrevious}
                  defaultValue={contactEmail}
                >
                  <Txt>Back</Txt>
                </Button>
              )}
            </>
          }
        >
          <TextInput
            id="firstName"
            title="First Name"
            placeholder="Enter First name"
            type="text"
            required
            autoComplete="given-name"
            defaultValue={firstName || currentFormData.firstName}
            pattern={NAME_REGEX.source}
            maxLength={30}
            overrideErrorMessage={customErrorMessages}
            hidden={Boolean(firstName && NAME_REGEX.test(firstName))}
          />
          <TextInput
            id="lastName"
            title="Last Name"
            placeholder="Enter Last name"
            type="text"
            required
            autoComplete="family-name"
            defaultValue={lastName || currentFormData.lastName}
            pattern={NAME_REGEX.source}
            maxLength={30}
            overrideErrorMessage={customErrorMessages}
            hidden={Boolean(lastName && NAME_REGEX.test(lastName))}
          />
          <TextInput
            id="email"
            title="Email Address"
            placeholder="Enter Email Address"
            type="text"
            readOnly
            defaultValue={contactEmail || currentFormData.username}
            hidden={!isSocial}
          />
          <hr className={classes.divider} />
          <label className={classes.labelOverrides}>
            <Txt>Date of birth</Txt>
          </label>
          <div className={classes.dateOfBirth}>
            <TextInput
              id="day"
              title="Day"
              placeholder="Day"
              type="number"
              min={1}
              max={31}
              required
              onInput={handleDOBChange}
              ref={dayRef}
              className={classes.inputOverrides}
              autoComplete="bday-day"
              defaultValue={currentFormData.dateOfBirth?.getUTCDate()}
              overrideErrorMessage={(_validity, _value) => {
                return undefined;
              }}
              onBlur={validateDOB}
            />
            <TextInput
              id="month"
              title="Month"
              type="number"
              placeholder="Month"
              min={1}
              max={12}
              required
              onInput={handleDOBChange}
              ref={monthRef}
              className={classes.inputOverrides}
              autoComplete="bday-month"
              defaultValue={
                currentFormData.dateOfBirth && currentFormData.dateOfBirth?.getUTCMonth() + 1
              }
              overrideErrorMessage={(_validity, _value) => {
                return undefined;
              }}
              onBlur={validateDOB}
            />
            <TextInput
              id="year"
              title="Year"
              placeholder="Year"
              type="number"
              min={new Date().getFullYear() - 99}
              max={new Date().getFullYear() - 14}
              required
              ref={yearRef}
              onInput={handleDOBChange}
              className={classes.inputOverrides}
              autoComplete="bday-year"
              defaultValue={currentFormData.dateOfBirth?.getUTCFullYear()}
              overrideErrorMessage={(_validity, _value) => {
                return undefined;
              }}
              onBlur={validateDOB}
            />
          </div>
          {dateError && (
            <Txt className={classes.errorMessage} component="p">
              {dateError}
            </Txt>
          )}
        </Form>
      </RegistrationCard>
    </div>
  );
}
